export const movdTokens = {
  color: {
    // New light, premium design system for quote flow redesign.
    // Reference: docs/design/movd-quote-flow-reference.png
    // Primary green brand color replaces blue, light canvas replaces dark.
    
    // LEGACY KEYS (preserved for homepage/backward compat — do not use in quote flow)
    ink: '#0B1220',
    inkSoft: '#12233A',
    paper: '#F4F6F8',
    paperMuted: '#E2E8F0',
    text: '#F8FAFC',
    textMuted: 'rgba(248,250,252,.66)',
    teal: '#176B60',  // Updated: green brand color
    tealSoft: '#D0E8E4',  // Updated: light mint
    warm: '#38BDF8',  // Kept for backward compat
    danger: '#DC2626',
    focus: '#176B60',  // Updated: green focus

    // SEMANTIC COLOR GROUPS (quote flow redesign palette)
    brand: {
      // Primary green brand identity
      primary: '#176B60',
      primaryHover: '#0F4C3A',
      primaryLight: '#D0E8E4',
      text: '#FFFFFF',
    },
    background: {
      // Light, premium canvas
      canvas: '#F8FAFB',
      muted: '#F1F5F9',
    },
    surface: {
      // White surfaces on light canvas
      base: '#FFFFFF',
      elevated: '#FFFFFF',
      sunken: '#F1F5F9',
      muted: '#F8FAFB',
    },
    form: {
      // Light form controls
      background: '#FFFFFF',
      border: '#D0E8E4',
      borderHover: '#176B60',
      borderFocus: '#176B60',
      placeholder: '#94A3B8',
    },
    content: {
      // Dark text on light backgrounds
      primary: '#0F172A',
      secondary: '#53636A',
      muted: '#94A3B8',
      tertiary: '#CBD5E1',
    },
    border: {
      subtle: '#E2E8F0',
      soft: '#D0E8E4',
      strong: '#176B60',
    },
    action: {
      // Green primary actions
      primary: '#176B60',
      primaryHover: '#0F4C3A',
      primaryText: '#FFFFFF',
      secondary: 'transparent',
      secondaryBorder: '#D0E8E4',
      secondaryText: '#0F172A',
      ghost: '#176B60',
    },
    state: {
      // Feedback colors
      success: '#16A34A',
      successSurface: '#DCFCE7',
      warning: '#D97706',
      warningSurface: '#FEF3C7',
      error: '#DC2626',
      errorSurface: '#FEE2E2',
      selected: '#FFFFFF',
      selectedBorder: '#176B60',
      disabled: '#B7BEC7',
      disabledSurface: '#EDEFF1',
    },
  },

  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    size: {
      // Quote flow typography scale (updated for light, premium aesthetic)
      xs: 11,      // labels, captions
      sm: 13,      // helper text
      base: 15,    // body text
      md: 16,      // primary body
      lg: 20,      // section headers
        xl: 28,      // stage titles
        xxl: 82,     // hero titles (clamp: 42-92px)
        xxxl: 92,    // confirmation hero
    },
    weight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
      black: 900,
      superblack: 950,
    },
    lineHeight: {
      tight: 0.88,
      snug: 1.05,
      normal: 1.5,
      relaxed: 1.7,
    },
    semantic: {
      display: 'clamp(2.5rem, 5vw, 4.25rem)',
      displayCompact: 'clamp(2rem, 4vw, 3rem)',
      heading: '1.5rem',
      subheading: '1.125rem',
      body: '1rem',
      small: '.8125rem',
      label: '.75rem',
      eyebrow: '.6875rem',
    },
  },
  space: {
    // Consistent spacing scale
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 24,
    6: 32,
    7: 48,
    8: 64,
  },
  radius: {
    // Subtle, rounded corners (premium aesthetic)
    none: 0,
    sm: 6,
    md: 10,
    lg: 16,
    xl: 24,
    full: 9999,
  },
  
  // Layout system for quote flow redesign
  size: {
    fieldHeight: 52,
    buttonHeight: 48,
    controlGap: 16,
    sectionGap: 32,
    contentMaxWidth: 1180,  // Updated from 1440
    formMaxWidth: 640,
    // Form field widths
    fieldFullWidth: '100%',
    fieldHalfWidth: '48%',
    fieldThirdWidth: '31%',
    fieldQuarterWidth: '23%',
    fieldSmallWidth: '140px',
  },
  
  layout: {
     // Quote flow section spacing (responsive using clamp)
     sectionPadding: 'clamp(28px, 6vw, 56px)',  // Responsive section padding
     headerPadding: 'clamp(28px, 6vw, 56px)',   // Header section padding
     cardPadding: 'clamp(22px, 4vw, 36px)',     // Card internal padding
    sectionSpacing: 32,
    fieldRowGap: 16,
    fieldColumnGap: 16,
    formContainerMaxWidth: 560,
    stageContentMarginTop: 24,
    containerMaxWidth: 1180,  // Primary layout container
    stageMaxWidth: 1080,
  },
  
  shadow: {
    // Subtle, premium elevation
    none: 'none',
    sm: '0 2px 6px rgba(15,23,42,0.06)',
    base: '0 6px 20px rgba(15,23,42,0.08)',
    md: '0 8px 28px rgba(15,23,42,0.10)',
    lg: '0 12px 36px rgba(15,23,42,0.14)',
    xl: '0 20px 52px rgba(15,23,42,0.16)',
    // Semantic names
    panel: '0 10px 30px rgba(15,23,42,0.12)',
    raised: '0 6px 18px rgba(15,23,42,0.08)',
    subtle: '0 2px 8px rgba(15,23,42,0.06)',
  },
  
  breakpoint: {
    xs: 390,
    sm: 560,
    md: 768,
    lg: 1024,
    xl: 1440,
  },
  
  motion: {
    micro: '120ms cubic-bezier(.22,1,.36,1)',
    standard: '220ms cubic-bezier(.22,1,.36,1)',
    relaxed: '350ms cubic-bezier(.22,1,.36,1)',
  },
};

export const movdFocus = {
  outline: `3px solid ${movdTokens.color.brand.primary}`,
  outlineOffset: 3,
};

